import { createFileRoute, Link } from "@tanstack/react-router";

import { MemberPortal } from "@/components/site/MemberPortal";
import { useAuth0, useMemberLogin, useMemberRoles } from "@/integrations/fc/auth0";

export const Route = createFileRoute("/members")({
  staticData: { sitemap: false },
  ssr: false,
  head: () => ({
    meta: [
      { title: "Members Area — Foreign Council On Economic Relations" },
      {
        name: "description",
        content: "Members area with meeting documentation and upcoming invitations.",
      },
      { property: "og:title", content: "Members Area — Foreign Council" },
      { property: "og:description", content: "Meeting documentation and upcoming invitations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MembersPage,
});

// UX gate only. The real authorization boundary is Postgres RLS in the Council's
// own database, which releases members-only rows exclusively to verified Auth0
// identities. No Supabase Auth session handling is permitted here.
function MembersPage() {
  const { isLoading, isAuthenticated, user } = useAuth0();
  const login = useMemberLogin();
  const { isAdmin } = useMemberRoles();

  return (
    <section className="bg-surface">
      <div className="mx-auto w-full max-w-[1400px] px-5 py-16 lg:px-8 lg:py-24">
        <h1 className="fc-h2 fc-rule text-foreground">Members Area</h1>

        {isLoading ? (
          <p className="mt-8 fc-body text-muted-foreground">Verifying your session…</p>
        ) : isAuthenticated ? (
          <>
            <p className="mt-8 max-w-[640px] fc-body text-muted-foreground">
              Welcome{user?.name ? `, ${user.name}` : ""}
              {user?.email ? ` (${user.email})` : ""}. Confirm your attendance for upcoming
              events with one click.
              {isAdmin ? " As an administrator you can also create and edit events and see registrations." : ""}
            </p>
            <MemberPortal isAdmin={isAdmin} />
            <Link
              to="/meetings"
              className="mt-12 inline-block border border-foreground px-7 py-3 text-base font-medium text-foreground transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Browse all meetings
            </Link>
          </>
        ) : (
          <>
            <p className="mt-8 max-w-[640px] fc-body text-muted-foreground">
              This area is reserved for members. Meeting documentation, invitations and
              members-only material are available after signing in.
            </p>
            <button
              type="button"
              onClick={() => login("/members")}
              className="mt-10 inline-block bg-primary px-7 py-3 text-base font-medium text-primary-foreground transition-opacity hover:opacity-85"
            >
              Go to member login
            </button>
          </>
        )}
      </div>
    </section>
  );
}
